/**
 * What fills the content region while a lazy screen's code is on its way.
 *
 * Every screen in this console is a separate chunk, so this is the only thing
 * on screen for as long as a first visit to a route takes. On the connections
 * this product is used on that is not instant, and a blank region reads as a
 * console that has broken rather than one that is working.
 *
 * `role="status"` with a name, because otherwise a screen reader user gets
 * nothing at all from a lazy route: the shell around them has not changed, the
 * heading they were expecting has not arrived, and there is no event to
 * announce. The bars themselves are `aria-hidden`, since reading out four
 * grey rectangles is worse than silence.
 *
 * The shape is a page header and a block, matching what almost every screen
 * here resolves into. A centred spinner was the alternative and it is worse in
 * one specific way: the content jumps from the middle of the region to the top
 * of it at the moment the screen arrives, which is the moment somebody has
 * started reading.
 */

import { Skeleton } from '@/components/primitives'
import styles from './ScreenLoading.module.css'

export interface ScreenLoadingProps {
  /**
   * What is being fetched, for the announcement. Defaults to the neutral word
   * because the router does not know the name of a screen it has not loaded.
   */
  label?: string
}

export function ScreenLoading({ label = 'Loading this screen' }: ScreenLoadingProps) {
  return (
    <div className={styles.loading} role="status">
      <span className="visually-hidden">{label}</span>

      <div className={styles.head} aria-hidden="true">
        <Skeleton width="14rem" height="2rem" />
        <Skeleton width="26rem" />
      </div>

      <div className={styles.body} aria-hidden="true">
        <Skeleton lines={4} />
      </div>
    </div>
  )
}
